import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { images, statistics } from '../../data/mock';
import { Button } from '../ui/button';
import {
  Target,
  Zap,
  Globe,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Shield,
  Clock,
} from 'lucide-react';
import AnimatedNumber from '../ui/AnimatedNumber';

const HomePage = () => {
  const { t, isRTL } = useLanguage();
  const location = useLocation();
  const isOnePage = location && location.pathname === '/one_page';

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {images.heroVideo ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
              aria-label={t ? t('home.heroVideoAria') : 'Hero video'}
            >
              <source src={images.heroVideo} type="video/mp4" />
              {/* Fallback to image if video not supported */}
              <img src={images.hero} alt={t ? t('home.heroImageAlt') : 'Oil exploration'} />
            </video>
          ) : (
            <img
              src={images.hero}
              alt={t ? t('home.heroImageAlt') : 'Oil exploration'}
              className="w-full h-full object-cover"
              style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
            />
          )}
          {/* Left → right black gradient (stronger on left, fading to right) */}
          <div
            className="absolute inset-0"
            style={{ backgroundImage: "linear-gradient(to right, #000000b2, transparent)" }}
          />
          {/* subtle top fade to keep text readable */}
          {/* <div
            className="absolute inset-0"
            style={{ backgroundImage: "linear-gradient(to top, #000000b2, transparent)" }}
          /> */}
        </div>


        {/* Hero Content */}
        <div
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32"
          style={{ paddingLeft: isRTL ? '36rem' : '1rem', paddingRight: isRTL ? '1rem' : '36rem' }}
        >
          <div className="max-w-3xl">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-8">
              <Shield className="h-4 w-4 text-red-500" />
              <span className="text-red-300 text-sm font-medium">
                {t('hero.trustBadge')}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.headline').split('.').map((part, i) => (
                <span key={i}>
                  {i === 0 ? (
                    <span className="text-red-500">{part}.</span>
                  ) : (
                    <span>{part}</span>
                  )}
                </span>
              ))}
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-10">
              {t('hero.subheadline')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
                {isOnePage ? (
                  <Button
                    size="lg"
                    onClick={() => window.dispatchEvent(new CustomEvent('onepage-scroll', { detail: 'technology' }))}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
                  >
                    {t('hero.primaryCta')}
                    <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                ) : (
                  <Link to="/technology">
                    <Button
                      size="lg"
                      className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
                    >
                      {t('hero.primaryCta')}
                      <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </Button>
                  </Link>
                )}
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-red-600 text-red-600 text-white  hover:bg-white/5 hover:bg-red-600 hover:border-red-600 px-8 py-6 text-lg rounded-lg"
              >
                <a href="/petroservies-new-profile.pdf" download>
                  {t('hero.secondaryCta')}
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
          <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-red-500 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Problem vs Solution */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Problem */}
            <div className="p-10 bg-zinc-900/30 border border-zinc-800 rounded-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-zinc-800 rounded-full text-gray-400 text-sm font-medium mb-6">
                {t('challenge.problemTitle')}
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                {t('challenge.problemDesc')}
              </p>
              <div className="mt-8 flex items-center gap-4 text-red-400">
                <div className="text-4xl font-bold">{t('challenge.riskAmount')}</div>
                <div className="text-sm">
                  {t('challenge.riskNote').split('\n').map((line, idx, arr) => (
                    <span key={idx}>
                      {line}
                      {idx < arr.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Solution */}
            <div className="p-10 bg-gradient-to-br from-red-950/40 to-zinc-900/50 border border-red-900/30 rounded-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/20 rounded-full text-red-400 text-sm font-medium mb-6">
                {t('challenge.solutionTitle')}
              </div>
              <p className="text-gray-200 text-lg leading-relaxed">
                {t('challenge.solutionDesc')}
              </p>
              <div className="mt-8 flex items-center gap-4 text-green-400">
                <CheckCircle2 className="h-8 w-8" />
                <div className="text-lg font-semibold">{t('challenge.solutionMetric')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Global Footprint */}
      <section className="py-24 bg-zinc-950 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-50">
          <img
            src={images.homescan}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-6">
              {t('footprint.title')}
            </h2>
            <p className=" max-w-3xl mx-auto text-lg leading-relaxed">
              {t('footprint.desc')}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-8 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl text-center">
              <div className="flex items-center justify-center gap-2 text-4xl font-bold text-red-500 mb-2">
                  <Clock className="h-8 w-8" />
                  <AnimatedNumber value={statistics.yearsActive} suffix="+" />
                </div>
              <div className="text-gray-400">{t('footprint.stat1')}</div>
            </div>

            <div className="p-8 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl text-center">
              <div className="flex items-center justify-center gap-2 text-4xl font-bold text-red-500 mb-2">
                <Target className="h-8 w-8" />
                <AnimatedNumber value={statistics.dryPlaceAccuracy} />
              </div>
              <div className="text-gray-400">{t('footprint.stat2')}</div>
            </div>

            <div className="p-8 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl text-center col-span-2 lg:col-span-1">
              <div className="flex items-center justify-center gap-2 text-4xl font-bold text-red-500 mb-2">
                <TrendingUp className="h-8 w-8" />
                <AnimatedNumber value={statistics.millionsSaved} />
              </div>
              <div className="text-gray-400">{t('footprint.stat3')}</div>
            </div>
          </div>

          {/* Countries List */}
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            {(t('footprint.countries') || []).map((country) => (
              <div
                key={country}
                className="px-6 py-3 bg-zinc-900/50 rounded-full text-gray-300"
              >
                {country}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-16">
            {t('promise.title')}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Accuracy */}
            <div className="group p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-red-500/30 transition-all duration-300">
              <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                <Target className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('promise.accuracy.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('promise.accuracy.desc')}
              </p>
            </div>

            {/* Efficiency */}
            <div className="group p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-red-500/30 transition-all duration-300">
              <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                <Zap className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('promise.efficiency.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('promise.efficiency.desc')}
              </p>
            </div>

            {/* Mobility */}
            <div className="group p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-red-500/30 transition-all duration-300">
              <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                <Globe className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {t('promise.mobility.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('promise.mobility.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* KH Group Section */}
      <section >
        <div className="relative overflow-hidden">
          {/* Background image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/images/KH_bg.png')" }}
            aria-hidden="true"
          />
          {/* Dark tint over background for contrast */}
          <div className="absolute inset-0 bg-black/30" aria-hidden="true" />

          {/* Full-section glass overlay (soft) */}
          <div
            className="absolute inset-0  bg-white/6"
            aria-hidden="true"
          />

          <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="rounded-2xl p-8 text-center">
              <img
                src="/images/KH_logo_no_bg.png"
                alt="KH Group"
                className="mx-auto h-24 w-auto mb-6"
              />

              <p className={`text-white text-lg sm:text-xl font-bold leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                {t('kh.line1')}
              </p>
              <p className={`text-whitemt-3 ${isRTL ? 'text-right' : 'text-center'} max-w-2xl mx-auto`}>
                {t('kh.line2')}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
            {t('cta.title')}
          </h2>
          {isOnePage ? (
            <Button
              size="lg"
              onClick={() => window.dispatchEvent(new CustomEvent('onepage-scroll', { detail: 'contact' }))}
              className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
            >
              {t('cta.button')}
              <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          ) : (
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
              >
                {t('cta.button')}
                <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;